import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IWizardStepRendererProps, StackPanel, Typography, useWizardStep } from '@agilepoint/wat-react-ux';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { IMobileApplicationDetails } from 'modules/create-new-app/queries';
import { MobileAppHelpLink, StyledStackPanel } from 'common';
import { WizardNavigationActions } from '../footers';
import { AppFiltersForm } from '../forms';


export interface IAppMetadata {
    appID: string;
    description: string;
    guid: number;
    name: string;
    value: string;
}

interface IAppFiltersProps extends IWizardStepRendererProps {
    appMetadata: IAppMetadata[];
    defaultAppFilters?: {
        appListOptions: string;
        showApps: {
            [key: string]: boolean;
        }
    }
    onSaveAppFilterDetails: (formData: IAppFiltersFormFields) => Promise<{
        UpdateMobileAppResult: IMobileApplicationDetails;
    }>
}

export interface IAppFiltersFormFields {
    appListOptions: string;
    showApps: {
        [key: string]: boolean;
    }
}

export const AppFilters = React.memo((props: IAppFiltersProps) => {
    const { stepId, appMetadata, defaultAppFilters, onSaveAppFilterDetails } = props;
    const { saveData, data } = useWizardStep(stepId);
    const translate = useTranslate();

    const form = useForm<IAppFiltersFormFields>({
        mode: 'onChange',
        defaultValues: {
            appListOptions: defaultAppFilters?.appListOptions || 'show-only-selected-below',
            showApps: defaultAppFilters?.showApps || {}
        }
    });

    const { trigger, getValues, reset } = form;

    const onNext = React.useCallback(async () => {
        if (!await trigger()) {
            return false;
        }
        const formData = getValues();

        return onSaveAppFilterDetails(formData).then((res: Awaited<ReturnType<typeof onSaveAppFilterDetails>>) => {
            saveData({ updateMobileAppRsponse: res.UpdateMobileAppResult, formData });
            return true;
        }).catch(() => false);
    }, [trigger, getValues, onSaveAppFilterDetails, saveData]);

    React.useEffect(() => {
        data && reset(data.formData);
    }, [reset, data]);

    return (
        <FormProvider {...form}>
            <MobileAppHelpLink id="appFilters" />
            <StyledStackPanel margin='30px 0px 0px 0px' alignment="center" width="100%" height="calc(100% - 50px)" orientation='vertical' gap="40">
                <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                    <Typography.H2 title={translate('modules.createNewApp.steps.appFilters.appFilterHeading')}><Localize translate='modules.createNewApp.steps.appFilters.appFilterHeading' /></Typography.H2>
                    <Typography.Medium title={translate('modules.createNewApp.steps.appFilters.appFilterSubtitle')}> <Localize translate='modules.createNewApp.steps.appFilters.appFilterSubtitle' /></Typography.Medium>
                </StackPanel>
                <AppFiltersForm appMetadata={appMetadata} />
            </StyledStackPanel>
            <WizardNavigationActions
                stepId={stepId}
                hook={onNext} />
        </FormProvider>
    );
});
